import {
  ComponentType,
  EAppSupportType,
  EProjectType,
  type IComponent,
  type IScreenTargetContext,
  isAppSupportTypeOfferedOnScreen,
  isReactNativeOnlyOnScreen
} from "@kuika/kuika-cl-model";
// Deep source import, deliberately: the shared rule is newer than the model package's BUNDLED
// build, and the bundle is what the bare package name resolves to at runtime. The `/src/` path is
// mapped to the model's sources by both the app build and the test runner (the convention several
// designer modules already use), so this needs no rebuild of the package to work.
import {
  structureOwnershipRoleOf,
  type StructureOwnershipRole
} from "@kuika/kuika-cl-model/src/models/component-structure-role";

/**
 * WHICH catalog components may be placed on a screen, and WHAT each one is.
 *
 * ## Why this is one function
 *
 * The rule already existed twice, character for character: the human component panel
 * (`left-panel/components-panel/component-toolkit`) and the AI's catalog tool
 * (`designer-component-catalog-actions`). The workflow screen builder needs the same answer for
 * its own palette, and a third copy is how the three drift — a component the catalog gains
 * tomorrow would appear in some palettes and not others, for no reason a user could explain. Both
 * original sites now call this; the rule lives here.
 *
 * ## The rule, and why each clause is there
 *
 *   - `isItChildItem` — a repeat slot the catalog nests INSIDE a host (a table's row, header,
 *     column). Placing one on a screen creates an orphan the platform has no place for.
 *   - `isItNavigationComponent` — navigation elements are authored through their own surfaces in
 *     this platform (a screen button is a project-level button screen), not dropped from a palette.
 *   - `isItHiddenFromComponentList` — the catalog's own "do not offer this" flag.
 *   - `isDeprecated` — kept for existing screens, never offered for new work.
 *   - `appSupportType` vs the project — `isAppSupportTypeOfferedOnScreen` (kuika-cl-model), the ONE
 *     platform predicate the drop/paste guards, the property panel and the MCP catalog also use: a
 *     Web-only component is not offered to a mobile project (unless the screen itself is a web
 *     screen), a Mobile-only one is not offered to a desktop project, and a `WebAndReactNative` one
 *     is offered on a mobile screen only while React Native generation is open to this user
 *     (`reactNativeOffered`, the RN_BETA_ALLOWLIST gate) — `isReactNativeOnlyComponent` says when
 *     the palette must badge it.
 *   - report vs screen — the two design modes have disjoint catalogs.
 *
 * The STRUCTURAL role of each entry (form field / button / data host / container) comes from
 * `structureOwnershipRoleOf` in the shared model, so the designer and the AI orchestrator classify
 * the same catalog the same way.
 */

export interface IPlaceableComponentsContext {
  /** The project the screen belongs to — decides the platform filter. */
  projectType?: EProjectType | number;
  /** True when the target screen is a web screen (a mobile project may still hold one). */
  isWebScreen: boolean;
  /** Report designer, not screen designer: the two catalogs are disjoint. */
  isReportMode: boolean;
  /**
   * Whether this user, in this workspace, may generate mobile platforms with React Native
   * (`isReactNativeGenerationOffered`). Decides whether a `WebAndReactNative` component is offered on
   * a MOBILE screen; omitted means offered (the union) and the publish validation stays the gate.
   */
  reactNativeOffered?: boolean;
}

function targetContextOf(component: IComponent, context: IPlaceableComponentsContext): IScreenTargetContext {
  return {
    appSupportType: component.appSupportType,
    projectType: context.projectType as EProjectType | undefined,
    isWebScreen: context.isWebScreen,
    reactNativeOffered: context.reactNativeOffered
  };
}

/**
 * WHY one catalog component may not be placed here, or `null` when it may.
 *
 * The AI's catalog tool must be able to say why a name it was asked for is unavailable, so the
 * agent stops retrying it. That explanation has to come from the same rule the palettes filter
 * by — a second copy that merely happened to agree today is exactly the drift this module was
 * created to end. `isPlaceableComponent` is this answer reduced to a boolean.
 */
export function placementExclusionReason(component: IComponent, context: IPlaceableComponentsContext): string | null {
  if (component.isItChildItem) return "it is a child component that is auto-created with its parent container";
  if (component.isItNavigationComponent) return "it is a navigation component managed by the screen shell";
  if (component.isItHiddenFromComponentList) return "it is hidden from the component list of this project";
  if (component.isDeprecated) return "it is deprecated";
  if (!isAppSupportTypeOfferedOnScreen(targetContextOf(component, context))) {
    if (component.appSupportType === EAppSupportType.Web) {
      return "it is web-only and the current screen is not a web screen";
    }
    if (component.appSupportType === EAppSupportType.Mobile) {
      return "it is mobile-only and this is a desktop project";
    }
    return "it renders on a mobile screen only through React Native generation, which is not offered here";
  }
  if (context.isReportMode && !component.isReportDesignComponent) {
    return "it is a screen component and the report designer is active";
  }
  if (!context.isReportMode && component.isReportDesignComponent) {
    return "it is a report-designer component and the screen designer is active";
  }
  return null;
}

/** Whether ONE catalog component may be placed on a screen in this context. */
export function isPlaceableComponent(component: IComponent, context: IPlaceableComponentsContext): boolean {
  return placementExclusionReason(component, context) === null;
}

/**
 * Whether this component renders on the target screen ONLY through the React Native generator (a
 * `WebAndReactNative` component on a Mobile project's mobile screen). Palettes badge these so a user
 * on a Native configuration learns before dropping, not from the publish error (KV1263) after.
 */
export function isReactNativeOnlyComponent(component: IComponent, context: IPlaceableComponentsContext): boolean {
  return isReactNativeOnlyOnScreen(targetContextOf(component, context));
}

/** The catalog, filtered to what may be placed. Order is the catalog's own. */
export function filterPlaceableComponents(
  components: readonly IComponent[] | undefined,
  context: IPlaceableComponentsContext
): IComponent[] {
  return (components ?? []).filter((component: IComponent): boolean => isPlaceableComponent(component, context));
}

/** One entry of a palette: what to show, and what it will be once placed. */
export interface IPaletteComponent {
  /** The catalog name — the identity `createNewComponentByName` is given. */
  name: string;
  /** The palette group the catalog itself declares. */
  categoryName: string;
  /** The catalog's icon name for this component. */
  icon: string | undefined;
  /** What it is structurally, from the shared model's rule. */
  role: StructureOwnershipRole;
}

/**
 * The palette, deduplicated by catalog name and sorted the way a person reads a list.
 *
 * Dedup is by NAME rather than id because a component set can carry more than one revision of the
 * same component; the palette must offer it once (the same choice the AI's catalog tool makes).
 */
export function buildComponentPalette(
  components: readonly IComponent[] | undefined,
  context: IPlaceableComponentsContext
): IPaletteComponent[] {
  const byName: Map<string, IPaletteComponent> = new Map<string, IPaletteComponent>();
  filterPlaceableComponents(components, context).forEach((component: IComponent): void => {
    const name: string = (component.name ?? "").trim();
    if (name.length === 0 || byName.has(name)) return;
    byName.set(name, {
      name,
      categoryName: (component.componentCategoryName ?? component.componentCategory ?? "Other").trim(),
      icon: component.icon,
      role: structureOwnershipRoleOf(component)
    });
  });
  return [...byName.values()].sort((left: IPaletteComponent, right: IPaletteComponent): number =>
    left.name.localeCompare(right.name)
  );
}

/**
 * Component types that ACCEPT children — the platform's own drop-target rule, previously a private
 * method of the human component panel (`isDroppableContainer`) and now shared with the workflow
 * screen builder, which has to answer the same question before it places anything.
 *
 * `simple` (a label, an input) and the repeat slots are absent on purpose: dropping into them
 * produces a tree the renderer cannot lay out.
 */
const DROP_TARGET_TYPES: ReadonlySet<ComponentType> = new Set<ComponentType>([
  ComponentType.abstractContainer,
  ComponentType.container,
  ComponentType.responsiveColumn,
  ComponentType.fixedFooter,
  ComponentType.fixedHeader,
  ComponentType.layoutFooter,
  ComponentType.layoutHeader,
  ComponentType.layoutLeft,
  ComponentType.layoutRight
]);

/** Whether a component of this type can hold a placed child. */
export function isDropTargetComponentType(componentType: ComponentType | undefined): boolean {
  if (componentType === undefined) return false;
  return DROP_TARGET_TYPES.has(componentType);
}
